import { Box, Divider, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FormType } from '@/api/enum/formType.enum';
import {
	Dispatch,
	forwardRef,
	SetStateAction,
	useEffect,
	useImperativeHandle,
} from 'react';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import SingleCheckbox from '@/common/components/form-inputs/SingleCheckbox';
import { useGetDicListQuery } from '@/api/common';
import AppAutocomplete from '@/common/components/form-inputs/AppAutoCompleteMenu';
import { eightDigitPhone } from '@/utils/validationHelper';

interface Props {
	type: FormType;
	details?: any;
	setType: Dispatch<SetStateAction<FormType>>;
}

const EditInfoFamilyMemberForm = forwardRef(
	({ type, setType, details }: Props, ref) => {
		const { t } = useTranslation();
		const defaultValues: DefaultValues<any> = {};

		const { data: relationshipDicList } = useGetDicListQuery('relationship', {
			refetchOnMountOrArgChange: true,
		});

		useEffect(() => {
			if (details) methods.reset(details);
		}, [details]);

		const formSchema = Yup.object().shape({
			familyTel: Yup.string()
				.nullable()
				.test(eightDigitPhone(t('errorMessage.phoneMustBeEightDigits'))),
		});
		const methods = useForm<any>({
			defaultValues,
			resolver: yupResolver(formSchema),
			mode: 'onChange',
			shouldFocusError: true,
		});

		useImperativeHandle(ref, () => ({
			getFormData: () => methods.getValues(),
			validateForm: () => {
				methods.trigger();
				methods.formState.isValid;
				return methods.trigger().then(() => methods.formState.isValid);
			},
		}));

		return (
			<>
				<FormProvider {...methods}>
					<Typography variant='h6' sx={{ fontWeight: 'bold' }} marginBottom={2}>
						{t('preAdmission.informationOfApplicantFamilyMemberCarer')}
					</Typography>
					<form onSubmit={methods.handleSubmit((e: any) => e.preventDefault())}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={12}>
								<Typography variant='body2' color='common.neutral40'>
									{t('preAdmission.prefix')}
								</Typography>
								<Box display={'flex'}>
									<SingleCheckbox
										control={methods.control}
										name='familyPrefixMr'
										label={t('preAdmission.mr')}
										handleChange={(checked: boolean) => {
											if (checked) {
												methods.setValue('familyPrefixMrs', false);
												methods.setValue('familyPrefixMs', false);
											}
										}}
									/>
									<SingleCheckbox
										control={methods.control}
										name='familyPrefixMrs'
										label={t('preAdmission.mrs')}
										handleChange={(checked: boolean) => {
											if (checked) {
												methods.setValue('familyPrefixMr', false);
												methods.setValue('familyPrefixMs', false);
											}
										}}
									/>
									<SingleCheckbox
										control={methods.control}
										name='familyPrefixMs'
										label={t('preAdmission.ms')}
										handleChange={(checked: boolean) => {
											if (checked) {
												methods.setValue('familyPrefixMr', false);
												methods.setValue('familyPrefixMrs', false);
											}
										}}
									/>
								</Box>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='familyNameEn'
									label={t('preAdmission.name_en')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='familyNameCh'
									label={t('preAdmission.name_zh')}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='familyTel'
									label={t('preAdmission.telNo')}
								/>
							</Grid>
							<Grid size={6}>
								<Typography variant='body2' color='common.neutral40'>
									{t('preAdmission.livingWithTheApplicant')}
								</Typography>
								<Box display={'flex'}>
									<SingleCheckbox
										control={methods.control}
										name='familyLivingWithApplicantYes'
										label={'Yes'}
										handleChange={(checked: boolean) => {
											if (checked)
												methods.setValue('familyLivingWithApplicantNo', false);
										}}
									/>
									<SingleCheckbox
										control={methods.control}
										name='familyLivingWithApplicantNo'
										label={'No'}
										handleChange={(checked: boolean) => {
											if (checked)
												methods.setValue('familyLivingWithApplicantYes', false);
										}}
									/>
								</Box>
							</Grid>
							<Grid size={6}>
								<AppAutocomplete
									control={methods.control}
									name='relationshipWithApplicant'
									label={t('preAdmission.relationshipWithApplicant')}
									options={(relationshipDicList?.data || []).map(
										(item: { labelEn: any }) => {
											return {
												...item,
												name: item.labelEn,
											};
										}
									)}
								/>
							</Grid>
						</Grid>
						<Divider />
						<Grid container spacing={3} marginTop={3} marginBottom={3}>
							<Grid size={12}>
								<Typography variant='body1'>
									{t('preAdmission.consentOfFamilyMemberCarer')}
								</Typography>
							</Grid>
							<Grid size={12}>
								<Typography variant='body2' color='common.neutral40'>
									{t('preAdmission.forReceivingICCMWServices')}
								</Typography>
								<SingleCheckbox
									sx={{
										flexShrink: 0,
									}}
									control={methods.control}
									name='familyConsentObtained'
									label={t('preAdmission.hasBeenObtained')}
									handleChange={(checked: boolean) => {
										if (checked)
											methods.setValue('familyConsentNotObtained', false);
									}}
								/>
								<Box>
									<SingleCheckbox
										sx={{
											flexShrink: 0,
										}}
										control={methods.control}
										name='familyConsentNotObtained'
										label={t('preAdmission.hasNotBeenObtained')}
										handleChange={(checked: boolean) => {
											if (checked)
												methods.setValue('familyConsentObtained', false);
										}}
									/>
								</Box>
							</Grid>
							<Grid size={12}>
								<Typography variant='body2' color='common.neutral40'>
									{t('preAdmission.thatICCMWProfessionalWorkersCanApproach')}
								</Typography>
								<Box display={'flex'}>
									<SingleCheckbox
										control={methods.control}
										name='familyIsHim'
										label={t('preAdmission.him')}
										handleChange={(checked: boolean) => {
											if (checked) methods.setValue('familyIsHer', false);
										}}
									/>
									<SingleCheckbox
										control={methods.control}
										name='familyIsHer'
										label={t('preAdmission.her')}
										handleChange={(checked: boolean) => {
											if (checked) methods.setValue('familyIsHim', false);
										}}
									/>
								</Box>
							</Grid>
						</Grid>
					</form>
				</FormProvider>
			</>
		);
	}
);

EditInfoFamilyMemberForm.displayName = 'EditInfoFamilyMemberForm';

export default EditInfoFamilyMemberForm;
